import { useQuery } from '@tanstack/react-query';

import api from '@/axios';
import { GET_PURCHASE_ORDER_DOCUMENTS_KEY } from '@/keys';
import type { Paginated } from '@/types/core/paginated';
import type { PurchaseOrderDocument } from '@/types/core/purchase-order/purchase-order-document';
import type { DataTableQueryVariables } from '@/types/data-table';

type GetPurchaseOrderDocumentsParams = Pick<
  DataTableQueryVariables<PurchaseOrderDocument>,
  'page' | 'perPage'
>;

type PurchaseOrderDocumentsResponse =
  | Paginated<PurchaseOrderDocument>
  | PurchaseOrderDocument[];

function isPaginatedPurchaseOrderDocumentsResponse(
  response: PurchaseOrderDocumentsResponse,
): response is Paginated<PurchaseOrderDocument> {
  return !Array.isArray(response);
}

export const getPurchaseOrderDocuments = async (
  id: string,
  { page = 1, perPage = 10 }: GetPurchaseOrderDocumentsParams,
): Promise<Paginated<PurchaseOrderDocument>> => {
  const response = await api.get<PurchaseOrderDocumentsResponse>(
    `/purchase-orders/${id}/documents`,
    {
      params: {
        page,
        perPage,
      },
    },
  );

  if (isPaginatedPurchaseOrderDocumentsResponse(response.data)) {
    return response.data;
  }

  const total = response.data.length;
  const maxPage = Math.ceil(total / perPage) || 1;
  const currentPage = Math.min(page, maxPage);
  const start = (currentPage - 1) * perPage;

  return {
    data: response.data.slice(start, start + perPage),
    currentPage,
    maxPage,
    total,
    perPage,
  };
};

export const useGetPurchaseOrderDocuments = ({
  id,
  page,
  perPage,
}: GetPurchaseOrderDocumentsParams & { id: string }) =>
  useQuery({
    queryKey: [...GET_PURCHASE_ORDER_DOCUMENTS_KEY, id, page, perPage],
    queryFn: () => getPurchaseOrderDocuments(id, { page, perPage }),
    enabled: Boolean(id),
  });
